import { supabase } from '@/integrations/supabase/client';
import { TOTAL_SKILLS, validateSkillsCount } from '@/lib/getTotalSkills';

export type ProgressSummary = {
  totalStars: number;
  skillsMastered: number;
  totalAchievements: number;
  totalMilestones: number;
  readinessPct: number;
  level: number;
};

/**
 * Calculate readiness percentage from skill scores (0-10 scale)
 * readiness% = average of all skill scores * 10
 * e.g., avg 7.5 => 75% ready
 */
export function computeReadinessPct(scores0to10: number[]): number {
  if (!scores0to10 || scores0to10.length === 0) return 0;
  
  const validScores = scores0to10.filter(s => !isNaN(s) && s >= 0 && s <= 10);
  if (validScores.length === 0) return 0;
  
  const avg = validScores.reduce((sum, score) => sum + score, 0) / validScores.length;
  const pct = Math.round(avg * 10);
  
  // Clamp between 0 and 100
  return Math.max(0, Math.min(100, pct));
}

/**
 * Calculate level based on readiness percentage
 * Level bands:
 * 0: 0–19
 * 1: 20–39
 * 2: 40–59
 * 3: 60–74
 * 4: 75–89
 * 5: 90–100
 */
export function computeLevel(readinessPct: number): number {
  if (isNaN(readinessPct) || readinessPct < 0) return 0;
  
  const clamped = Math.max(0, Math.min(100, readinessPct));
  
  if (clamped >= 90) return 5;
  if (clamped >= 75) return 4;
  if (clamped >= 60) return 3;
  if (clamped >= 40) return 2;
  if (clamped >= 20) return 1;
  return 0;
}

/**
 * Fetch total stars for a student
 * Calculates from student_skills_progress (sum of all current_score values)
 */
export async function fetchTotalStars(studentId: string): Promise<number> {
  try {
    const { data, error } = await supabase
      .from('student_skills_progress')
      .select('current_score')
      .eq('student_id', studentId);

    if (error) throw error;
    
    const total = (data || []).reduce((sum, row) => sum + (row.current_score || 0), 0);
    return Math.max(0, total);
  } catch (error) {
    console.error('Error fetching total stars:', error);
    return 0;
  }
}

/**
 * Fetch achievement/milestone/mastered counts for a student
 */
export async function fetchCounts(studentId: string): Promise<{
  achievements: number;
  milestones: number;
  skillsMastered: number;
}> {
  try {
    // Fetch achievements
    const { data: achievementsData, error: achievementsError } = await supabase
      .from('student_achievements')
      .select('*')
      .eq('student_id', studentId);

    if (achievementsError) {
      console.error('❌ Error fetching achievements:', achievementsError);
      throw achievementsError;
    }

    // Fetch milestones (achievements with type 'milestone')
    const { data: milestonesData, error: milestonesError } = await supabase
      .from('student_achievements')
      .select('*')
      .eq('student_id', studentId)
      .eq('achievement_type', 'milestone');

    if (milestonesError) {
      console.error('❌ Error fetching milestones:', milestonesError);
      throw milestonesError;
    }

    // Fetch mastered skills (score = 10)
    const { data: masteredData, error: masteredError } = await supabase
      .from('student_skills_progress')
      .select('*')
      .eq('student_id', studentId)
      .eq('current_score', 10);

    if (masteredError) {
      console.error('❌ Error fetching mastered skills:', masteredError);
      throw masteredError;
    }

    // Log the actual data

    return {
      achievements: achievementsData?.length || 0,
      milestones: milestonesData?.length || 0,
      skillsMastered: masteredData?.length || 0,
    };
  } catch (error) {
    console.error('Error fetching counts:', error);
    return {
      achievements: 0,
      milestones: 0,
      skillsMastered: 0,
    };
  }
}

/**
 * Fetch complete progress summary for a student
 * Single source of truth for all progress calculations
 */
export async function fetchProgressSummary(studentId: string): Promise<ProgressSummary> {
  try {
    // Get total number of DVSA skills for validation
    // Dev-only sanity check (it only logs a warning) — skipped in production to save a request.
    if (import.meta.env.DEV) {
      const { data: allSkillsData } = await supabase
        .from('dvsa_skills')
        .select('id')
        .eq('is_visible', true)
        .eq('is_deleted', false);
      validateSkillsCount(allSkillsData?.length || 0);
    }

    // Use centralized constant
    const totalSkills = TOTAL_SKILLS;

    // Fetch skill scores and achievements once each (in parallel), then derive
    // every figure from those two results instead of re-querying.
    const [progressRes, achievementsRes] = await Promise.all([
      supabase
        .from('student_skills_progress')
        .select('current_score')
        .eq('student_id', studentId),
      supabase
        .from('student_achievements')
        .select('achievement_type')
        .eq('student_id', studentId),
    ]);

    const { data: progressData, error: progressError } = progressRes;
    if (progressError) throw progressError;

    const scores = (progressData || []).map(p => p.current_score || 0);
    
    // Calculate total stars (sum of all scores)
    const totalStars = scores.reduce((sum, score) => sum + score, 0);

    // Create array with ALL skills (fill missing skills with 0)
    const allScores = new Array(TOTAL_SKILLS).fill(0);
    scores.forEach((score, index) => {
      if (index < totalSkills) {
        allScores[index] = score;
      }
    });

    // Calculate readiness percentage based on ALL skills
    const readinessPct = computeReadinessPct(allScores);

    // Calculate level from readiness
    const level = computeLevel(readinessPct);

    // Counts (same rules as fetchCounts: errors on achievements yield zeros)
    const achievementRows = achievementsRes.error ? [] : (achievementsRes.data || []);
    if (achievementsRes.error) console.error('❌ Error fetching achievements:', achievementsRes.error);

    const summary: ProgressSummary = {
      totalStars,
      skillsMastered: scores.filter(s => s === 10).length,
      totalAchievements: achievementRows.length,
      totalMilestones: achievementRows.filter(a => a.achievement_type === 'milestone').length,
      readinessPct,
      level,
    };

    // Dev logging

    return summary;
  } catch (error) {
    console.error('Error fetching progress summary:', error);
    return {
      totalStars: 0,
      skillsMastered: 0,
      totalAchievements: 0,
      totalMilestones: 0,
      readinessPct: 0,
      level: 0,
    };
  }
}
